import type { BusinessGoalV1, ExecutionModeV1 } from "@adpilot/shared";
import { IconCheck, IconShieldCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { UnitInput } from "@/components/patterns/UnitInput";
import { ChoiceCard, ModeCard } from "./ai-policy-cards";
import { GOAL_OPTIONS } from "./ai-policy-options";

export function PolicyStep({
  goal,
  setGoal,
  targetAcos,
  setTargetAcos,
  validAcos,
  recommendedRange,
  mode,
  selectMode,
}: {
  goal: BusinessGoalV1;
  setGoal: (goal: BusinessGoalV1) => void;
  targetAcos: string;
  setTargetAcos: (targetAcos: string) => void;
  validAcos: boolean;
  recommendedRange: string;
  mode: ExecutionModeV1;
  selectMode: (mode: ExecutionModeV1) => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex min-h-full flex-col gap-[13px] px-[22px] py-[17px]">
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-[11px] font-semibold text-ink">{t("policyDialog.businessGoal")}</h3>
          <span className="text-[9px] text-muted-foreground">{t("policyDialog.chooseOne")}</span>
        </div>
        <div
          className="grid grid-cols-2 gap-2"
          role="radiogroup"
          aria-label={t("policyDialog.businessGoal")}
        >
          {GOAL_OPTIONS.map((option) => (
            <ChoiceCard
              key={option.value}
              selected={goal === option.value}
              title={t(option.labelKey)}
              description={t(option.descriptionKey)}
              onClick={() => setGoal(option.value)}
            />
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-2 text-[12px] font-semibold leading-4 text-ink">
          {t("policyDialog.targetAcos")}
        </h3>
        <div className="flex h-[34px] items-center gap-2.5">
          <UnitInput
            aria-invalid={!validAcos}
            aria-label={t("policyDialog.targetAcos")}
            value={targetAcos}
            onChange={(event) => setTargetAcos(event.target.value)}
            className="w-[130px] shrink-0"
            inputMode="decimal"
            unit="%"
          />
          <span className="flex h-7 items-center gap-1.5 rounded-full border border-green/15 bg-green/5 px-2.5 text-[10px] font-medium leading-[13px] text-green-11">
            <IconCheck className="size-3" />{" "}
            {t("policyDialog.recommendedRange", { range: recommendedRange })}
          </span>
          <span className="text-[10px] leading-[14px] text-muted-foreground">
            {t("policyDialog.basedOnLast30")}
          </span>
        </div>
      </section>
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-[11px] font-semibold text-ink">{t("policyDialog.executionMode")}</h3>
          <span className="text-[9px] text-muted-foreground">
            {t("policyDialog.executionModeHint")}
          </span>
        </div>
        <div
          className="grid grid-cols-3 gap-2"
          role="radiogroup"
          aria-label={t("policyDialog.executionMode")}
        >
          <ModeCard
            selected={mode === "recommendation"}
            title={t("policyDialog.mode.recommendation")}
            description={t("policyDialog.modeCards.recommendationDesc")}
            onClick={() => selectMode("recommendation")}
          />
          <ModeCard
            selected={mode === "semi-auto"}
            title={t("policyDialog.mode.semiAuto")}
            badge={t("policyDialog.modeCards.recommended")}
            description={t("policyDialog.modeCards.semiAutoDesc")}
            onClick={() => selectMode("semi-auto")}
          />
          <ModeCard
            selected={mode === "full-auto"}
            title={t("policyDialog.mode.fullAuto")}
            description={t("policyDialog.modeCards.fullAutoDesc")}
            onClick={() => selectMode("full-auto")}
          />
        </div>
      </section>
      <div className="flex h-[52px] shrink-0 items-start gap-3 rounded-[9px] border border-line bg-subtle px-3 py-2.5">
        <IconShieldCheck className="mt-0.5 size-3.5 shrink-0 text-ink" stroke={1.8} />
        <div>
          <p className="text-[10px] font-semibold leading-4 text-ink">
            {t("policyDialog.guardrails.title")}
          </p>
          <p className="text-[9px] leading-4 text-muted-foreground">
            {t("policyDialog.guardrails.description")}
          </p>
        </div>
      </div>
    </div>
  );
}
